/* VB engraving: default product template (`vb_engraving` block in main-product).

   Behaviour port of the blade template's configurator, so a shopper meets the
   same control on an axe as on a seax. Markup uses the blade CSS class names on
   purpose (vb-engraving.css copies those rules); behaviour hooks are data-vbe*
   so the two scripts can never bind the same node.

   Adds hand-etched engraving to any product whose blade.engravable (or legacy
   blade.engraving) metafield is true. The engraving TEXT travels as line-item
   properties; the CHARGE is a separate hidden fee product added as a second
   cart line, the same contract the blade template uses, so the smith receives
   an identical order shape from both templates.

   Depends on vb-runes.js for window.VBRunes.transliterate (shared with the
   blade template; do not re-implement the transliteration here).
*/
(function () {
  'use strict';

  function init(root) {
    if (!root || root.dataset.vbeReady === '1') return;
    root.dataset.vbeReady = '1';

    var head = root.querySelector('[data-vbe-toggle]');
    var input = root.querySelector('[data-vbe-input]');
    var count = root.querySelector('[data-vbe-count]');
    var hint = root.querySelector('[data-vbe-hint]');
    var scriptWrap = root.querySelector('[data-vbe-scripts]');
    var placeWrap = root.querySelector('[data-vbe-places]');
    var preview = root.querySelector('[data-vbe-preview]');
    var pvText = root.querySelector('[data-vbe-previewtext]');
    var pvScript = root.querySelector('[data-vbe-previewscript]');
    var pvPlace = root.querySelector('[data-vbe-previewplace]');

    var feeVariant = parseInt(root.dataset.feeVariant, 10) || 0;
    var maxLen = parseInt(root.dataset.maxLength, 10) || 20;

    var on = false;
    var script = 'Latin';
    var place = '';
    if (placeWrap) {
      var first = placeWrap.querySelector('[aria-pressed="true"]');
      place = first ? (first.dataset.value || first.textContent).trim() : '';
    }

    /* All three rows come from vb-runes.js (window.VBRunes), the same engine the
       rune translator uses, so preview and translator cannot disagree.
       Latin is deliberately absent: no key means no runes. */
    var SCRIPT_KEYS = {
      'Elder Futhark': 'elder',
      'Younger Futhark': 'younger',
      'Anglo-Saxon': 'anglo'
    };
    function scriptKey(label) { return SCRIPT_KEYS[label] || null; }
    function futhark(s, key) {
      return (window.VBRunes && key)
        ? window.VBRunes.transliterate(String(s || ''), key)
        : String(s || '');
    }

    function text() {
      return input ? input.value.trim() : '';
    }

    /* Engraving only counts once there is something to engrave; an empty box
       must never add the fee. */
    function active() {
      return on && text().length > 0;
    }

    function render() {
      root.dataset.on = on ? 'true' : 'false';
      if (head) head.setAttribute('aria-expanded', on ? 'true' : 'false');

      var t = text();
      var runeKey = scriptKey(script);
      var isRunic = !!runeKey;

      if (count) count.textContent = t.length + ' / ' + maxLen;
      if (hint) {
        hint.dataset.filled = t.length > 0 ? 'true' : 'false';
        var key = !t.length ? 'hintEmpty' : isRunic ? 'hintFuthark' : 'hintLatin';
        if (hint.dataset[key]) hint.textContent = hint.dataset[key];
      }

      if (preview) {
        preview.hidden = !(on && t.length > 0);
        if (pvText) {
          pvText.textContent = isRunic ? futhark(t, runeKey) : t.toUpperCase();
          pvText.classList.toggle('vb-bl-engrave__previewtext--runic', isRunic);
        }
        if (pvScript) pvScript.textContent = script;
        if (pvPlace) pvPlace.textContent = place;
      }

      /* A dynamic-checkout order skips the cart, so the fee line would never be
         added and the engraving would ship unpaid. Hide Buy-it-now while the
         engraving is live rather than silently losing the charge. */
      var payWrap = document.querySelector('.shopify-payment-button');
      if (payWrap) payWrap.style.display = active() ? 'none' : '';
    }

    function props() {
      var p = {};
      var t = text();
      if (!t) return p;
      p['Engraving'] = t; // raw as typed, the smith's source of truth
      p['Script'] = script;
      if (place) p['Engraving placement'] = place;
      var cartKey = scriptKey(script);
      if (cartKey) p['Engraving runes'] = futhark(t, cartKey);
      return p;
    }

    if (head) {
      head.addEventListener('click', function () {
        on = !on;
        render();
        if (on && input) input.focus();
      });
    }

    if (input) {
      input.setAttribute('maxlength', String(maxLen));
      input.addEventListener('input', render);
    }

    function wireChips(wrap, onPick) {
      if (!wrap) return;
      wrap.addEventListener('click', function (e) {
        var btn = e.target.closest('button[aria-pressed]');
        if (!btn || !wrap.contains(btn)) return;
        wrap.querySelectorAll('button[aria-pressed]').forEach(function (b) {
          b.setAttribute('aria-pressed', b === btn ? 'true' : 'false');
        });
        onPick((btn.dataset.value || btn.textContent).trim());
        render();
      });
    }

    wireChips(scriptWrap, function (v) { script = v; });
    wireChips(placeWrap, function (v) { place = v; });

    /* ---------- add to cart ----------
       One /cart/add.js call carrying both lines, so the blade and the fee can
       never end up in the cart separately. Both lines carry the same properties
       so either line alone tells fulfilment what to etch. */
    /* Bind to THIS product's form. The old `form[action*="/cart/add"]` matched Dawn's installment
       form first (the price block renders it above the buy buttons), so the handler sat on a form
       that is never submitted and every engraving was lost (order #VB49861 got the fee, no text). */
    var form = (root.dataset.formId && document.getElementById(root.dataset.formId)) ||
      document.querySelector('product-form form[action*="/cart/add"]');
    if (form) {
      form.addEventListener(
        'submit',
        function (e) {
          if (!active() || !feeVariant) return; // let Dawn handle it normally

          e.preventDefault();
          e.stopImmediatePropagation();

          /* form.elements also holds inputs linked with form="…" (Dawn's quantity box, the custom ring size) */
          var idField = form.elements.namedItem('id');
          var qtyField = form.elements.namedItem('quantity');
          var id = idField ? parseInt(idField.value, 10) : 0;
          var qty = qtyField ? parseInt(qtyField.value, 10) || 1 : 1;
          if (!id) return;

          /* keep any other line-item properties the form carries */
          var itemProps = {};
          new FormData(form).forEach(function (v, k) {
            var m = /^properties\[(.+)\]$/.exec(k);
            if (m && typeof v === 'string' && v !== '') itemProps[m[1]] = v;
          });
          var eng = props();
          Object.keys(eng).forEach(function (k) { itemProps[k] = eng[k]; });

          var payload = { items: [
            { id: id, quantity: qty, properties: itemProps },
            { id: feeVariant, quantity: qty, properties: eng }
          ] };

          var btn = form.querySelector('[type="submit"]');
          if (btn) btn.setAttribute('aria-busy', 'true');

          fetch((window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/') + 'cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify(payload)
          })
            .then(function (r) { return r.json().then(function (d) { return { ok: r.ok, data: d }; }); })
            .then(function (res) {
              if (!res.ok) throw new Error((res.data && res.data.description) || 'Add to cart failed');
              /* Pilot: go to the cart so both lines are plainly visible.
                 Swap for Dawn's drawer refresh once the flow is signed off. */
              var buyNow = form.hasAttribute('data-vb-checkout');
              form.removeAttribute('data-vb-checkout');
              window.location.href = (window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/') + (buyNow ? 'checkout' : 'cart');
            })
            .catch(function (err) {
              if (btn) btn.removeAttribute('aria-busy');
              form.removeAttribute('data-vb-checkout');
              document.querySelectorAll('[data-vb-buynow][aria-busy]').forEach(function (b) { b.removeAttribute('aria-busy'); });
              console.error('[vb-engraving]', err);
              alert('Sorry, we could not add the engraving. Please try again.');
            });
        },
        true // capture, so this runs before Dawn's product-form handler
      );
    }

    render();
  }

  function boot() {
    document.querySelectorAll('[data-vbe]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  document.addEventListener('shopify:section:load', boot);
})();
